"use client";

import { useEffect, useState } from "react";
import { recoverFromChunkError } from "@/lib/chunkRecovery";
import { reportClientError } from "@/lib/telemetry";

/**
 * Last-resort boundary — replaces the root layout when it fails, so it must
 * render its own <html>/<body> and carry inline styles (globals.css and the
 * font pipeline may be part of what broke).
 */
export default function GlobalError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  // Most of the ChunkLoadErrors in telemetry landed *here* — the root
  // boundary, which replaces the whole app with this screen. A stale chunk
  // from a deploy that happened mid-visit is fixed by reloading, so do that
  // rather than asking the visitor to retry something that cannot succeed.
  const [recovering] = useState(() => recoverFromChunkError(error));

  useEffect(() => {
    console.error(error);
    reportClientError(error, "global", { recovered: String(recovering) });
  }, [error, recovering]);

  // Still a full document — global-error replaces the root layout — but an
  // empty one, because the reload is already on its way.
  if (recovering) {
    return (
      <html lang="en">
        <body style={{ margin: 0, background: "#f5f0e6" }} />
      </html>
    );
  }

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f0e6",
          color: "#202522",
          fontFamily: "system-ui, sans-serif",
          textAlign: "center",
          padding: "24px",
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <title>Something went wrong · Rulebound</title>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 600, margin: 0 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 15, color: "#6a6a5f", lineHeight: 1.6, marginTop: 10 }}>
            Rulebound hit an unexpected error. Your books and notes are safe — this is on us.
          </p>
          <button
            onClick={() => unstable_retry()}
            style={{
              marginTop: 22,
              padding: "11px 22px",
              borderRadius: 12,
              border: "none",
              background: "#29483d",
              color: "#fcfaf5",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          {error.digest && (
            <p style={{ marginTop: 16, fontSize: 11, color: "#9a9a8c" }}>
              Error reference: <code>{error.digest}</code>
            </p>
          )}
        </div>
      </body>
    </html>
  );
}
